import { useState } from 'react';
import { useAeThex } from '../../components/AeThexProvider';

// Map desktop theme names to web app themes
const THEMES = [
  { id: 'cyberpunk', label: 'Cyberpunk', preview: '#0a0a0c' },
  { id: 'og',        label: 'OG',        preview: '#1a1a1a' },
  { id: 'midnight',  label: 'Midnight',  preview: '#0d0d1a' },
  { id: 'stealth',   label: 'Stealth',   preview: '#111111' },
  { id: 'inferno',   label: 'Inferno',   preview: '#1a0a00' },
];

const SettingsPanel = ({ isOpen, onClose, onToggleActivity }) => {
  const { user, logout } = useAeThex();
  const [activeTab, setActiveTab] = useState('account');
  const [theme, setTheme] = useState(
    () => localStorage.getItem('aethex-theme') || 'cyberpunk'
  );
  const [accentColor, setAccentColor] = useState('#6366f1');
  const [notifications, setNotifications] = useState({
    desktop: true,
    sounds: true,
    messages: true,
    mentions: true,
  });

  if (!isOpen) return null;

  const applyTheme = (t) => {
    setTheme(t);
    document.documentElement.setAttribute('data-theme', t);
    localStorage.setItem('aethex-theme', t);
  };

  const tabs = [
    {
      id: 'account',
      label: 'My Account',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      ),
    },
    {
      id: 'appearance',
      label: 'Appearance',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
        </svg>
      ),
    },
    {
      id: 'voice',
      label: 'Voice & Video',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      ),
    },
    {
      id: 'notifications',
      label: 'Notifications',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    },
    {
      id: 'keybinds',
      label: 'Keybinds',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
        </svg>
      ),
    },
    {
      id: 'advanced',
      label: 'Advanced',
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
    },
  ];

  const accentColors = [
    { name: 'Indigo', value: '#6366f1' },
    { name: 'Purple', value: '#a855f7' },
    { name: 'Pink', value: '#ec4899' },
    { name: 'Red', value: '#ef4444' },
    { name: 'Orange', value: '#f97316' },
    { name: 'Green', value: '#22c55e' },
    { name: 'Cyan', value: '#06b6d4' },
    { name: 'Blue', value: '#3b82f6' },
  ];

  const renderContent = () => {
    switch (activeTab) {
      case 'account': {
        const displayName = user?.display_name || user?.username || 'User';
        const initial = displayName[0]?.toUpperCase() || 'U';
        const tier = user?.premium_tier || 'free';
        const tierLabel = tier === 'enterprise' ? 'ENTERPRISE' : tier === 'premium' ? 'PRO' : 'FREE';
        const tierColor = tier === 'enterprise' ? 'text-amber-400 bg-amber-400/10 border-amber-400/30' : tier === 'premium' ? 'text-indigo-400 bg-indigo-500/20 border-indigo-500/30' : 'text-gray-400 bg-white/5 border-white/10';
        const memberSince = user?.created_at ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : null;

        return (
          <div className="space-y-6">
            {/* Profile Card */}
            <div className="bg-gradient-to-br from-indigo-600/20 to-purple-600/20 rounded-2xl p-6 border border-white/10">
              <div className="flex items-start gap-4">
                <div className="relative flex-shrink-0">
                  {user?.avatar_url ? (
                    <img
                      src={user.avatar_url}
                      alt={displayName}
                      className="w-20 h-20 rounded-full object-cover border-2 border-white/10"
                      onError={(e) => { e.target.style.display='none'; e.target.nextSibling.style.display='flex'; }}
                    />
                  ) : null}
                  <div
                    className="w-20 h-20 rounded-full bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center text-3xl font-bold"
                    style={{ display: user?.avatar_url ? 'none' : 'flex' }}
                  >
                    {initial}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="text-xl font-bold text-white truncate">{displayName}</h3>
                  <p className="text-gray-400 text-sm">@{user?.username}</p>
                  <div className="flex items-center gap-2 mt-2 flex-wrap">
                    <span className={`px-2 py-0.5 text-xs rounded-full border font-semibold ${tierColor}`}>
                      {tierLabel}
                    </span>
                    {memberSince && (
                      <span className="text-gray-500 text-sm">Member since {memberSince}</span>
                    )}
                    {user?.role && user.role !== 'user' && (
                      <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 text-xs rounded-full border border-rose-500/30 font-semibold uppercase">
                        {user.role}
                      </span>
                    )}
                  </div>
                  {user?.custom_status && (
                    <p className="text-gray-500 text-sm mt-1 italic truncate">"{user.custom_status}"</p>
                  )}
                </div>
              </div>
            </div>

            {/* Account Details */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Account Details</h4>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <label className="text-xs text-gray-500 uppercase">Display Name</label>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-white">{displayName}</span>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <label className="text-xs text-gray-500 uppercase">Username</label>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-white">@{user?.username || '—'}</span>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <label className="text-xs text-gray-500 uppercase">Email</label>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-white">{user?.email || '—'}</span>
                </div>
              </div>

              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <label className="text-xs text-gray-500 uppercase">User ID</label>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-gray-500 text-xs font-mono truncate">{user?.id || '—'}</span>
                </div>
              </div>

              {user?.verified_domain && (
                <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                  <label className="text-xs text-gray-500 uppercase">Verified Domain</label>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-white flex items-center gap-2">
                      <span className="text-green-400">✓</span> {user.verified_domain}
                    </span>
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Status</h4>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { value: 'online', label: 'Online', color: '#22c55e' },
                  { value: 'idle', label: 'Idle', color: '#f59e0b' },
                  { value: 'dnd', label: 'Do Not Disturb', color: '#ef4444' },
                  { value: 'offline', label: 'Invisible', color: '#6b7280' },
                ].map((s) => (
                  <button
                    key={s.value}
                    className={`flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                      (user?.status || 'online') === s.value
                        ? 'bg-white/10 border-white/20'
                        : 'bg-white/5 border-white/10 hover:bg-white/8'
                    }`}
                  >
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background: s.color }} />
                    <span className="text-sm text-gray-300">{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Danger Zone */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-red-400 uppercase tracking-wider">Danger Zone</h4>
              <div className="bg-red-500/10 rounded-xl p-4 border border-red-500/20">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-white font-medium">Log Out</p>
                    <p className="text-gray-500 text-sm">Sign out of your account on this device</p>
                  </div>
                  <button
                    onClick={logout}
                    className="px-4 py-2 bg-red-600 hover:bg-red-500 rounded-lg text-sm text-white transition-colors flex-shrink-0"
                  >
                    Log Out
                  </button>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case 'appearance':
        return (
          <div className="space-y-6">
            {/* Theme Selection */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Theme</h4>
              <div className="grid grid-cols-3 gap-3">
                {THEMES.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => applyTheme(t.id)}
                    className={`p-4 rounded-xl border transition-all ${
                      theme === t.id
                        ? 'bg-indigo-500/20 border-indigo-500/50'
                        : 'bg-white/5 border-white/10 hover:border-white/20'
                    }`}
                  >
                    <div
                      className="w-full h-16 rounded-lg mb-2"
                      style={{ background: t.preview }}
                    />
                    <span className="text-sm text-gray-300 capitalize">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Accent Color */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Accent Color</h4>
              <div className="flex gap-3 flex-wrap">
                {accentColors.map((color) => (
                  <button
                    key={color.value}
                    onClick={() => setAccentColor(color.value)}
                    className={`w-10 h-10 rounded-full transition-all ${
                      accentColor === color.value ? 'ring-2 ring-white ring-offset-2 ring-offset-[#0a0a0c]' : ''
                    }`}
                    style={{ backgroundColor: color.value }}
                    title={color.name}
                  />
                ))}
              </div>
            </div>

            {/* Message Display */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Message Display</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                  <div>
                    <p className="text-white">Compact Mode</p>
                    <p className="text-gray-500 text-sm">Reduce spacing between messages</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-white/10 peer-focus:ring-2 peer-focus:ring-indigo-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                  <div>
                    <p className="text-white">Show Timestamps</p>
                    <p className="text-gray-500 text-sm">Display time next to messages</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-white/10 peer-focus:ring-2 peer-focus:ring-indigo-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Font Size */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Chat Font Size</h4>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <input
                  type="range"
                  min="12"
                  max="20"
                  defaultValue="14"
                  className="w-full accent-indigo-500"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-2">
                  <span>12px</span>
                  <span>14px</span>
                  <span>16px</span>
                  <span>18px</span>
                  <span>20px</span>
                </div>
              </div>
            </div>

            {/* Activity Panel toggle */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Panels</h4>
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                <div>
                  <p className="text-white">Activity Panel</p>
                  <p className="text-gray-500 text-sm">Show friends and live activity sidebar</p>
                </div>
                <button
                  onClick={() => { onToggleActivity && onToggleActivity(); onClose(); }}
                  className="px-4 py-2 bg-indigo-600/20 hover:bg-indigo-600/40 rounded-lg text-sm text-indigo-300 transition-colors border border-indigo-500/30"
                >
                  Toggle
                </button>
              </div>
            </div>
          </div>
        );

      case 'voice':
        return (
          <div className="space-y-6">
            {/* Input Device */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Input Device</h4>
              <select className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500/50">
                <option>Default - Microphone Array</option>
                <option>USB Audio Device</option>
                <option>Headset Microphone</option>
              </select>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Input Volume</span>
                  <span className="text-sm text-gray-400">75%</span>
                </div>
                <input type="range" defaultValue="75" className="w-full accent-indigo-500" />
              </div>
            </div>

            {/* Output Device */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Output Device</h4>
              <select className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-white focus:outline-none focus:border-indigo-500/50">
                <option>Default - Speakers</option>
                <option>USB Audio Device</option>
                <option>Headphones</option>
              </select>
              <div className="bg-white/5 rounded-xl p-4 border border-white/10">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-gray-400">Output Volume</span>
                  <span className="text-sm text-gray-400">100%</span>
                </div>
                <input type="range" defaultValue="100" className="w-full accent-indigo-500" />
              </div>
            </div>

            {/* Voice Settings */}
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Voice Settings</h4>
              <div className="space-y-3">
                <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                  <div>
                    <p className="text-white">Noise Suppression</p>
                    <p className="text-gray-500 text-sm">Powered by Krisp AI</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-white/10 peer-focus:ring-2 peer-focus:ring-indigo-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                  <div>
                    <p className="text-white">Echo Cancellation</p>
                    <p className="text-gray-500 text-sm">Reduce echo during calls</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" defaultChecked className="sr-only peer" />
                    <div className="w-11 h-6 bg-white/10 peer-focus:ring-2 peer-focus:ring-indigo-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                  <div>
                    <p className="text-white">Push to Talk</p>
                    <p className="text-gray-500 text-sm">Hold a key to transmit voice</p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" className="sr-only peer" />
                    <div className="w-11 h-6 bg-white/10 peer-focus:ring-2 peer-focus:ring-indigo-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
              </div>
            </div>

            {/* Test Audio */}
            <div className="bg-gradient-to-r from-indigo-600/20 to-purple-600/20 rounded-xl p-4 border border-indigo-500/20">
              <div className="flex items-center gap-4">
                <button className="w-12 h-12 bg-indigo-600 hover:bg-indigo-500 rounded-full flex items-center justify-center transition-colors">
                  <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                  </svg>
                </button>
                <div>
                  <p className="text-white font-medium">Mic Test</p>
                  <p className="text-gray-400 text-sm">Click to test your microphone</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'notifications':
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Desktop Notifications</h4>
              <div className="space-y-3">
                {Object.entries(notifications).map(([key, value]) => (
                  <div key={key} className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                    <div>
                      <p className="text-white capitalize">{key.replace(/([A-Z])/g, ' $1')}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={() => setNotifications({ ...notifications, [key]: !value })}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-white/10 peer-focus:ring-2 peer-focus:ring-indigo-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Do Not Disturb */}
            <div className="bg-orange-500/10 rounded-xl p-4 border border-orange-500/20">
              <div className="flex items-center gap-4">
                <div className="w-10 h-10 bg-orange-500/20 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                  </svg>
                </div>
                <div className="flex-1">
                  <p className="text-white font-medium">Do Not Disturb</p>
                  <p className="text-gray-400 text-sm">Mute all notifications</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-white/10 peer-focus:ring-2 peer-focus:ring-orange-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>
            </div>
          </div>
        );

      case 'keybinds':
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Keybinds</h4>
              {[
                { action: 'Toggle Mute', keys: ['Ctrl', 'Shift', 'M'] },
                { action: 'Toggle Deafen', keys: ['Ctrl', 'Shift', 'D'] },
                { action: 'Push to Talk', keys: ['`'] },
                { action: 'Quick Switcher', keys: ['Ctrl', 'K'] },
                { action: 'Search', keys: ['Ctrl', 'F'] },
                { action: 'Settings', keys: ['Ctrl', ','] },
                { action: 'Mark as Read', keys: ['Esc'] },
              ].map((bind, i) => (
                <div key={i} className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                  <span className="text-white">{bind.action}</span>
                  <div className="flex items-center gap-1">
                    {bind.keys.map((key, j) => (
                      <span key={j} style={{ display: 'contents' }}>
                        {j > 0 && <span className="text-gray-600">+</span>}
                        <kbd className="px-2 py-1 bg-white/10 rounded text-sm text-gray-300 border border-white/10">{key}</kbd>
                      </span>
                    ))}
                    <button className="ml-3 text-indigo-400 hover:text-indigo-300 text-sm">Edit</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'advanced':
        return (
          <div className="space-y-6">
            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Developer Mode</h4>
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                <div>
                  <p className="text-white">Developer Mode</p>
                  <p className="text-gray-500 text-sm">Show IDs and developer options</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" className="sr-only peer" />
                  <div className="w-11 h-6 bg-white/10 peer-focus:ring-2 peer-focus:ring-indigo-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Experimental</h4>
              <div className="flex items-center justify-between bg-white/5 rounded-xl p-4 border border-white/10">
                <div>
                  <p className="text-white">Hardware Acceleration</p>
                  <p className="text-gray-500 text-sm">Use GPU for rendering (requires restart)</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input type="checkbox" defaultChecked className="sr-only peer" />
                  <div className="w-11 h-6 bg-white/10 peer-focus:ring-2 peer-focus:ring-indigo-500/50 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-indigo-600"></div>
                </label>
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="text-sm font-medium text-gray-400 uppercase tracking-wider">Data</h4>
              <div className="grid grid-cols-2 gap-3">
                <button className="p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-left transition-colors">
                  <p className="text-white font-medium">Export Data</p>
                  <p className="text-gray-500 text-sm">Download your data</p>
                </button>
                <button className="p-4 bg-white/5 hover:bg-white/10 rounded-xl border border-white/10 text-left transition-colors">
                  <p className="text-white font-medium">Clear Cache</p>
                  <p className="text-gray-500 text-sm">Free up space</p>
                </button>
              </div>
            </div>

            {/* Version Info */}
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-medium">AeThex Connect</p>
                  <p className="text-gray-500 text-sm">Version 1.0.0-beta</p>
                </div>
                <button className="text-indigo-400 hover:text-indigo-300 text-sm">Check for Updates</button>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-5xl h-[85vh] bg-[#0a0a0c] rounded-2xl overflow-hidden border border-white/10 shadow-2xl flex">
        {/* Sidebar */}
        <div className="w-56 bg-white/[0.02] border-r border-white/10 p-4 flex flex-col">
          <h2 className="text-lg font-bold text-white px-3 mb-4">Settings</h2>

          <nav className="flex-1 space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  activeTab === tab.id
                    ? 'bg-white/10 text-white'
                    : 'text-gray-400 hover:text-white hover:bg-white/5'
                }`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>

          {/* Logout Button */}
          <div className="pt-4 border-t border-white/10">
            <button
              onClick={() => {
                logout();
                onClose();
              }}
              className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-all"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
              Log Out
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <h3 className="text-xl font-bold text-white">
              {tabs.find((t) => t.id === activeTab)?.label}
            </h3>
            <button
              onClick={onClose}
              className="w-8 h-8 bg-white/5 hover:bg-white/10 rounded-lg flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPanel;
